import { IAPIRoutes } from "../../../../config";

export type IPatientRepository = ReturnType<typeof PatientRepositoryFactory>;

export const PatientRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, POST, PUT } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const getPatientListAPI: IGetPatientListAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientList.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientEventListAPI: IGetPatientEventListAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientEventList
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.resident_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientSummaryAPI: IGetPatientSummaryAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientSummary
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientCircadiaVitalAPI: IGetPatientCircadiaVitalAPI = async (
    payload
  ) =>
    GET(
      APIRoutes.apiGetPatientCircadiaVital
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientPCCVitalAPI: IGetPatientPCCVitalAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientPCCVital
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientVitalGraphAPI: IGetPatientVitalGraphAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientVitalGraph
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.resident_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientRRGraphAPI: IGetPatientRRGraphAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientRRGraph
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientNotesAPI: IGetPatientNotesAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientNotes
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientNotificationSettingsAPI: IGetPatientNotificationSettingsAPI =
    async (payload) =>
      GET(
        APIRoutes.apiGetPatientNotificationSettings
          .replace("{clinic_id}", payload.clinic_id?.toString())
          .replace("{resident_id}", payload.patient_id?.toString()),
        payload,
        {
          Authorization: `Bearer ${await getAuthenticationToken()}`,
        }
      );

  const getDiagnosesAPI: IGetDiagnosesAPI = async (payload) =>
    GET(
      APIRoutes.apiGetDiagnoses
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{resident_id}", payload.resident_id.toString()),
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientListV2API: IGetPatientListV2API = async (payload) =>
    GET(
      APIRoutes.apiGetPatientListV2,
      {
        clinic_id: payload.clinic_id,
        patient_id: payload.patient_id,
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const saveDiagnosisAPI: ISaveDiagnosisAPI = async (payload) =>
    PUT(
      APIRoutes.apiSaveDiagnosis
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString()),
      payload.diagnoses,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPCCNotesAPI: IGetPatientPCCNotesAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientPCCNotes
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  // Monthly-window PCC notes (v4 endpoint). The path uses `{patient_id}`
  // (not `{resident_id}` like the older endpoint above) — keep them
  // separate so we don't mismask one for the other when reading.
  const getPCCNotesByMonthAPI: IGetPatientPCCNotesByMonthAPI = async (
    payload
  ) =>
    GET(
      APIRoutes.apiGetPatientPCCNotesByMonth
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{patient_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientHmacAPI: IGetPatientHmacAPI = async (payload) =>
    GET(APIRoutes.apiGetUserHmac, payload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

  const addPatientNoteAPI: IAddPatientNoteAPI = async (payload) =>
    POST(
      APIRoutes.apiAddPatientNote
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const generateCircadiaIQAPI: IGetGenerateCircadiaIQAPI = async (payload) =>
    POST(
      APIRoutes.apiGenerateCircadiaIQ
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{patient_id}", payload.patient_id?.toString()),
      {
        type: payload.type,
        date: payload.date,
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getCircadiaIQAPI: IGetCircadiaIQAPI = async (payload) =>
    GET(
      APIRoutes.apiGetCircadiaIQ
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{patient_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getOrderIQAPI: IGetOrderIQAPI = async (payload) =>
    GET(
      APIRoutes.apiGetOrderIQ
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{patient_id}", payload.patient_id?.toString()),
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getCircadiaIQForRecertificationAPI: IGetCircadiaIQForRecertificationAPI =
    async (payload) =>
      GET(
        APIRoutes.apiGetCircadiaIQForRecertification
          .replace("{clinic_id}", payload.clinic_id?.toString())
          .replace("{patient_id}", payload.patient_id?.toString()),
        {},
        {
          Authorization: `Bearer ${await getAuthenticationToken()}`,
        }
      );

  const getPatientListDashboardAPI: IGetPatientListDashboardAPI = async (
    payload
  ) =>
    GET(
      APIRoutes.apiGetPatientListDashboard.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      {
        floor_ids: payload.floorIds,
        no_of_days: payload.no_of_days,
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const registerPatientDeviceAPI: IRegisterPatientDeviceAPI = async (payload) =>
    POST(
      APIRoutes.apiRegisterPatientDevice.replace(
        "{clinic_id}",
        payload.clinic_id.toString()
      ),
      { device_id: payload.device_id },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const assignPatientDeviceAPI: IAssignPatientDeviceAPI = async (payload) =>
    POST(
      APIRoutes.apiPatientAssignedDevice
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString()),
      {
        device_id: payload.device_id,
        name: payload.name,
        device_type: payload.device_type,
        status: payload.status,
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const removePatientDeviceAPI: IRemovePatientDeviceAPI = async (payload) =>
    POST(
      APIRoutes.apiRemovePatientDevice
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{device_id}", encodeURIComponent(payload.device_id)),
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientAssignedDeviceAPI: IGetPatientAssignedDeviceAPI = async (
    payload
  ) =>
    GET(
      APIRoutes.apiPatientAssignedDevice
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString()),
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientDeviceConnectionStatusAPI: IGetPatientDeviceConnectionStatusAPI =
    async (payload) =>
      GET(
        APIRoutes.apiPatientDeviceConnectionStatus.replace(
          "{device_id}",
          encodeURIComponent(payload.device_id)
        ),
        {},
        {
          Authorization: `Bearer ${await getAuthenticationToken()}`,
        }
      );

  const replaceCohortPathParams = (payload: {
    clinic_id: number;
    patient_id: number;
    admission_id: number;
  }) =>
    APIRoutes.apiVnsPatientAdmissionCohort
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString())
      .replace("{admission_id}", payload.admission_id.toString());

  const getVnsPatientAdmissionCohortAPI: IGetVnsPatientAdmissionCohortAPI =
    async (payload) =>
      GET(
        replaceCohortPathParams(payload),
        {},
        {
          Authorization: `Bearer ${await getAuthenticationToken()}`,
        }
      );

  const updateVnsPatientAdmissionCohortAPI: IUpdateVnsPatientAdmissionCohortAPI =
    async (payload) =>
      PUT(
        replaceCohortPathParams(payload),
        { cohort: payload.cohort },
        {
          Authorization: `Bearer ${await getAuthenticationToken()}`,
        }
      );

  return {
    getPatientListAPI,
    getPatientEventListAPI,
    getPatientSummaryAPI,
    getVnsPatientAdmissionCohortAPI,
    updateVnsPatientAdmissionCohortAPI,
    getPatientCircadiaVitalAPI,
    getPatientPCCVitalAPI,
    getPatientVitalGraphAPI,
    getPatientRRGraphAPI,
    getPatientNotesAPI,
    getPatientNotificationSettingsAPI,
    getDiagnosesAPI,
    getPatientListV2API,
    saveDiagnosisAPI,
    getPCCNotesAPI,
    getPCCNotesByMonthAPI,
    addPatientNoteAPI,
    getPatientHmacAPI,
    generateCircadiaIQAPI,
    getCircadiaIQAPI,
    getOrderIQAPI,
    getCircadiaIQForRecertificationAPI,
    getPatientListDashboardAPI,
    registerPatientDeviceAPI,
    assignPatientDeviceAPI,
    removePatientDeviceAPI,
    getPatientAssignedDeviceAPI,
    getPatientDeviceConnectionStatusAPI,
  };
};
